import { apiService } from 'core/services/apiService';

export interface LogbookSalesMetric {
	id: number;
	name: string;
	description?: string | null;
	displayOrder: number;
	active?: boolean;
	category: string;
	metricType?: string;
}

export interface LogbookSalesMetricPreference {
	id: number;
	workplace: string; // IRI: /api/workplaces/:id
	metric: string | LogbookSalesMetric; // Can be IRI string or expanded object
	active: boolean;
}

const parseIriId = (iri?: string | null): number | null => {
	if (!iri) return null;
	const parts = iri.toString().split('/').filter(Boolean);
	const last = parts[parts.length - 1];
	const n = Number(last);
	return Number.isFinite(n) ? n : null;
};

// Extract metric ID from either IRI string or expanded object
const extractMetricId = (metric: string | LogbookSalesMetric | any): number | null => {
	if (!metric) return null;
	
	// If it's an expanded object with direct id property
	if (typeof metric === 'object' && metric.id) {
		const id = Number(metric.id);
		return Number.isFinite(id) ? id : null;
	}
	
	// If it's an object with @id property (JSON-LD format)
	if (typeof metric === 'object' && metric['@id']) {
		return parseIriId(metric['@id']);
	}
	
	// If it's a string IRI
	if (typeof metric === 'string') {
		return parseIriId(metric);
	}
	
	return null;
};

export const fetchLogbookSalesMetrics = async (): Promise<LogbookSalesMetric[]> => {
	const query = `logbook_sales_metrics?page=1&itemsPerPage=500&pagination=false`;
	const data = apiService.responseHandler(await apiService.get(query));
	
	// Support both member and hydra:member response formats
	if (!data || typeof data !== 'object') return [];
	const items = data.member ?? data['hydra:member'];
	return Array.isArray(items) ? items : [];
};

export const fetchLogbookSalesMetricPreferences = async (
	workplaceId: number,
): Promise<LogbookSalesMetricPreference[]> => {
	if (!workplaceId) return [];
	const query = `logbook_sales_metric_preferences?page=1&itemsPerPage=500&pagination=false&workplace=${workplaceId}`;
	const data = apiService.responseHandler(await apiService.get(query));
	
	// Support both member and hydra:member response formats
	if (!data || typeof data !== 'object') return [];
	const items = data.member ?? data['hydra:member'];
	return Array.isArray(items) ? items : [];
};

export const upsertLogbookSalesMetricPreference = async ({
	workplaceId,
	metricId,
	active,
}: {
	workplaceId: number;
	metricId: number;
	active: boolean;
}): Promise<any> => {
	const payload = {
		workplace: `/api/workplaces/${workplaceId}`,
		metric: `/api/logbook_sales_metrics/${metricId}`,
		active,
	};

	// Backend is expected to upsert based on (workplace, metric)
	return apiService.responseHandler(await apiService.post('logbook_sales_metric_preferences', payload));
};

export const buildLogbookSalesMetricPreferenceMap = (
	preferences: LogbookSalesMetricPreference[] | undefined,
): Map<number, LogbookSalesMetricPreference> => {
	const map = new Map<number, LogbookSalesMetricPreference>();
	if (!preferences || !Array.isArray(preferences)) return map;
	
	preferences.forEach((p) => {
		if (!p || typeof p !== 'object') return;
		// Use extractMetricId to handle both IRI string and expanded object
		const metricId = extractMetricId(p.metric);
		if (metricId == null) return;
		map.set(metricId, p);
	});
	return map;
};

